import { Chip, PageTree } from "../../../ds";
import type { PageTreeLock, PageTreeNode } from "../../../ds";
import { ReviewItem, ReviewKind, ReviewTreeNode } from "../utils/reviewModel";
import { ReviewKindDot, kindLabels, kindOrder } from "./ReviewKindBadge";
import { useCallback, useMemo } from "react";

import { LockMark } from "../../locks/models/LockDTO";

/** What the update check found for a page, shown as a pill: conflicts left, all resolved, merges cleanly, changed by the last update. */
export type TreeStatus =
    | { kind: "conflicts"; count: number }
    | { kind: "resolved" }
    | { kind: "merges" }
    | { kind: "changed" };

interface IReviewTreeProps {
    tree: ReviewTreeNode[];
    selectedKey?: string;
    onSelect: (key: string) => void;
    lockOf: (item: ReviewItem) => LockMark | undefined;
    /** The page's update status; when absent, the item's own flags show (stale, failed, applied). */
    statusOf?: (item: ReviewItem) => TreeStatus | undefined;
}

const flags: { when: (item: ReviewItem) => boolean; css: string; label: string; glyph: string }[] = [
    { when: (i) => i.stale, css: "is-stale", label: "Changed since the proposal", glyph: "!" },
    { when: (i) => i.outcome === "failed", css: "is-failed", label: "Failed", glyph: "✕" },
    { when: (i) => i.outcome === "applied", css: "is-applied", label: "Applied", glyph: "✓" },
    { when: (i) => i.outcome === "skipped", css: "is-skipped", label: "Not applied", glyph: "–" },
];

/** The row's marks: the item's own flags ("!" changed since, outcome), then the update status (conflict bubble or a pill). */
function Flags(props: { item: ReviewItem; status?: TreeStatus }) {
    const { status } = props;
    // the "!" stays next to a status pill (the board: "! RESOLVED"); the outcome glyphs only show on closed change requests
    const own = flags.filter((f) => f.when(props.item) && (!status || f.css === "is-stale"));
    let pill: React.ReactNode = null;
    if (status?.kind === "conflicts") {
        const label = `${status.count} conflict${status.count === 1 ? "" : "s"} with edits made since`;
        pill = <span className="cr-tree-bubble" role="img" aria-label={label} title={label}>{status.count}</span>;
    } else if (status) {
        const text = status.kind === "resolved" ? "resolved" : status.kind === "merges" ? "merges" : "changed";
        const label = status.kind === "resolved" ? "Conflicts resolved" : status.kind === "merges" ? "Edits made since merge cleanly" : "Changed by the last update";
        pill = (
            <span role="img" aria-label={label} title={label}>
                <Chip size="sm" tone={status.kind === "changed" ? "warning" : "success"}>{text}</Chip>
            </span>
        );
    }
    return (
        <>
            {own.map((f) => (
                <span key={f.css} className={`cr-tree-flag ${f.css}`} role="img" aria-label={f.label} title={f.label}>{f.glyph}</span>
            ))}
            {pill}
        </>
    );
}

interface Mapped {
    nodes: PageTreeNode[];
    items: Map<string, ReviewItem>;
}

/** The review tree as the design system's page tree: unchanged ancestors are context rows, deleted pages struck. */
function toPageTree(tree: ReviewTreeNode[]): Mapped {
    const items = new Map<string, ReviewItem>();
    const map = (nodes: ReviewTreeNode[]): PageTreeNode[] =>
        nodes.map((node) => {
            if (node.item) items.set(node.key, node.item);
            return {
                id: node.key,
                title: node.item ? node.item.title : node.title,
                children: node.children.length ? map(node.children) : undefined,
                context: !node.item,
                struck: node.item?.kind === "deleted",
            };
        });
    return { nodes: map(tree), items };
}

/**
 * The changed pages where they sit in the space, on the design system's page tree (read-only, as the reader shows the space): unchanged
 * ancestors dimmed for context, each changed page with its lock, its flags and its kind as a coloured dot, and a legend of the kinds.
 */
export function ReviewTree(props: IReviewTreeProps) {
    const { onSelect, lockOf, statusOf } = props;
    const mapped = useMemo(() => toPageTree(props.tree), [props.tree]);

    const locks = useMemo(() => {
        const out: Record<string, PageTreeLock | undefined> = {};
        mapped.items.forEach((item, key) => {
            const mark = lockOf(item);
            if (mark) out[key] = { state: mark.own ? "own" : "inherited", by: mark.lockTitle };
        });
        return out;
    }, [mapped, lockOf]);

    const rowFlags = useCallback((node: PageTreeNode) => {
        const item = mapped.items.get(node.id);
        if (!item) return null;
        return (
            <span className="cr-tree-flags">
                <Flags item={item} status={statusOf?.(item)} />
                <ReviewKindDot kind={item.kind} />
            </span>
        );
    }, [mapped, statusOf]);

    const kinds = useMemo(() => {
        const present = new Set<ReviewKind>();
        mapped.items.forEach((item) => present.add(item.kind));
        return kindOrder.filter((k) => present.has(k));
    }, [mapped]);

    return (
        <nav className="cr-tree" aria-label="Changed pages">
            <PageTree
                nodes={mapped.nodes}
                title="Changed pages"
                selectedId={props.selectedKey}
                onSelect={(id) => onSelect(id)}
                defaultExpanded
                readOnly
                filterPlaceholder="Filter changed pages"
                flags={rowFlags}
                locks={locks}
                emptyState={<p className="ark-muted">No changed page matches.</p>}
            />
            {kinds.length ? (
                <ul className="cr-tree-legend" aria-label="Kinds of change">
                    {kinds.map((k) => (
                        <li key={k}><span aria-hidden="true"><ReviewKindDot kind={k} /></span>{kindLabels[k]}</li>
                    ))}
                </ul>
            ) : null}
        </nav>
    );
}
